import { useState } from 'react';
import { CreditCard } from 'lucide-react';
import { openRazorpayBookingPayment } from '../utils/razorpayCheckout';
import {
  bookingRemainingDueAmount,
  userCanPayRemainingOnline
} from '../utils/bookingPayment';
import { onlineTotalWithGatewayFee } from '../utils/paymentGateway';

const formatInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

/**
 * Razorpay checkout for post-booking remaining balance.
 * @param {{ booking: object, className?: string, variant?: 'booking'|'dashboard'|'confirmed', onPaid?: () => void|Promise<void>, disabled?: boolean }} props
 */
export default function PayRemainingButton({
  booking,
  className = '',
  variant = 'booking',
  onPaid,
  disabled = false
}) {
  const [paying, setPaying] = useState(false);
  const canPay = userCanPayRemainingOnline(booking) && !disabled;
  const remaining = bookingRemainingDueAmount(booking);
  const onlinePay = onlineTotalWithGatewayFee(remaining);

  if (!canPay) return null;

  const handleClick = async () => {
    if (paying || !booking?.id) return;
    setPaying(true);
    try {
      await openRazorpayBookingPayment({
        booking,
        paymentFor: 'remaining',
        onSuccess: async () => {
          if (typeof onPaid === 'function') await onPaid();
        }
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Payment failed. Please try again.';
      if (!String(msg).toLowerCase().includes('cancelled by user')) {
        window.alert(msg);
      }
    } finally {
      setPaying(false);
    }
  };

  if (variant === 'confirmed') {
    return (
      <button
        type="button"
        className={`booking-confirmed-btn primary booking-progress-online-btn booking-progress-online-btn--gateway ${className}`.trim()}
        onClick={handleClick}
        disabled={paying}
      >
        <span className="booking-progress-online-btn-inner">
          <span className="booking-progress-online-btn-line1">
            <CreditCard size={18} aria-hidden />
            <span>
              {paying ? 'Opening payment…' : (
                <>
                  Pay Remaining — <strong>{formatInr(onlinePay.payableTotal)}</strong>
                </>
              )}
            </span>
          </span>
          <span className="booking-progress-online-btn-line2">
            Balance {formatInr(remaining)} + 2% gateway ({formatInr(onlinePay.gatewayCharge)})
          </span>
        </span>
      </button>
    );
  }

  const variantClass =
    variant === 'dashboard' ? 'btn-pay-remaining btn-pay-remaining--dashboard' : 'btn-pay-remaining';

  return (
    <button
      type="button"
      className={`${variantClass} ${className}`.trim()}
      onClick={handleClick}
      disabled={paying}
    >
      {paying ? 'Opening payment…' : `Pay Remaining ${formatInr(onlinePay.payableTotal)}`}
    </button>
  );
}
